import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from "@roberttamas/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Save } from "@/components/icons";
import i18n from "@/i18n";
import { getBillingConfig, setBillingDefaults } from "@/lib/api";
import { toAppError } from "@/lib/errors";
import type { GetBillingConfigResp, InvoiceSupplier } from "@/lib/types";

/** Sentinel for "no supplier selected" — Select items can't be "". */
const NONE = "__none__";

function supplierLabel(s: InvoiceSupplier) {
  return `${s.name} (${s.ico})`;
}

/**
 * Picks which supplier (billing entity in the invoice system) issues
 * orders, per currency. The list is fetched through our backend — the
 * browser never talks to the invoice system directly. An unset currency
 * blocks orders in it, so it is called out loudly.
 */
export function BillingPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [defaults, setDefaults] = useState<Record<string, string> | null>(null);

  const query = useQuery<GetBillingConfigResp>({
    queryKey: ["billingConfig"],
    queryFn: getBillingConfig,
  });

  useEffect(() => {
    if (query.data) setDefaults({ ...query.data.defaults });
  }, [query.data]);

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!defaults) throw new Error(i18n.t("billing.notLoaded"));
      return setBillingDefaults({ defaultSupplierByCurrency: defaults });
    },
    onSuccess: async () => {
      toast.success(t("billing.saved"));
      await queryClient.invalidateQueries({ queryKey: ["billingConfig"] });
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  if (query.isError) {
    return (
      <p className="text-sm text-destructive">
        {t("billing.loadFailed")}
      </p>
    );
  }

  if (query.isLoading || !defaults || !query.data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const { suppliers, currencies } = query.data;
  const dirty =
    JSON.stringify(defaults) !== JSON.stringify(query.data.defaults);
  const missing = currencies.filter((c) => !defaults[c]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("billing.eyebrow")}
        title={t("billing.title")}
        subtitle={t("billing.subtitle")}
        actions={
          <Button
            disabled={saveMutation.isPending || !dirty}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? (
              t("common.saving")
            ) : (
              <>
                <Save className="h-4 w-4" />
                {t("common.save")}
              </>
            )}
          </Button>
        }
      />

      {missing.length > 0 && (
        <p className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {t("billing.missing", { currencies: missing.join(", "), count: missing.length })}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("billing.defaultSupplierTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {currencies.map((currency) => {
            const eligible = suppliers.filter((s) =>
              s.currencies.includes(currency),
            );
            const value = defaults[currency] ?? "";
            // A saved supplier that is no longer eligible (archived, or its
            // account was unlinked) must stay visible so the admin sees what
            // is currently configured — flagged, not silently dropped.
            const stale =
              value !== "" && !eligible.some((s) => s.supplierId === value);
            return (
              <div key={currency} className="space-y-1.5">
                <Label className="flex items-center gap-2">
                  {currency}
                  {stale && (
                    <Badge variant="destructive">
                      {t("billing.staleBadge")}
                    </Badge>
                  )}
                </Label>
                {eligible.length === 0 && !stale ? (
                  <p className="text-sm text-muted-foreground">
                    {t("billing.noEligible", { currency })}
                  </p>
                ) : (
                  <Select
                    value={value === "" ? NONE : value}
                    onValueChange={(v) =>
                      setDefaults((prev) => {
                        const next = { ...prev };
                        if (v === NONE) delete next[currency];
                        else next[currency] = v;
                        return next;
                      })
                    }
                  >
                    <SelectTrigger className="w-full max-w-md">
                      <SelectValue placeholder={t("billing.selectSupplier")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>{t("billing.noneOption")}</SelectItem>
                      {stale && (
                        <SelectItem value={value}>
                          {t("billing.unavailableOption", { value })}
                        </SelectItem>
                      )}
                      {eligible.map((s) => (
                        <SelectItem key={s.supplierId} value={s.supplierId}>
                          {supplierLabel(s)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      <p className="text-sm text-muted-foreground">
        {t("billing.footer")}
      </p>
    </div>
  );
}
